import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { toast } from 'sonner';

interface ExamTab {
  id: string;
  enabled: boolean;
}

const defaultTabs: ExamTab[] = [
  { id: 'history', enabled: true },
  { id: 'physical', enabled: true },
  { id: 'eye', enabled: true },
  { id: 'hearing', enabled: true },
  { id: 'dental', enabled: true },
];

// Categories to exclude (removed from clinical exams)
const excludedCategories = ['nutrition', 'exercises', 'sleep', 'mood', 'relations', 'addictions'];

export function ExamTabsManagement() {
  const [tabs, setTabs] = useState<ExamTab[]>(defaultTabs);
  const [loading, setLoading] = useState(true);
  const { t, dir } = useLanguage();

  useEffect(() => {
    const fetchTabs = async () => {
      try {
        const tabsDoc = await getDoc(doc(db, 'settings', 'examTabs'));
        if (tabsDoc.exists()) {
          const dbTabs = tabsDoc.data().tabs;
          // Filter out excluded categories
          const filteredTabs = dbTabs.filter((tab: ExamTab) => !excludedCategories.includes(tab.id));
          setTabs(filteredTabs);
          
          // Update database if we filtered out any tabs
          if (filteredTabs.length !== dbTabs.length) {
            await setDoc(doc(db, 'settings', 'examTabs'), { tabs: filteredTabs });
            console.log('Removed excluded categories from exam tabs:', excludedCategories);
          }
        }
      } catch (error) {
        console.error('Error fetching exam tabs:', error);
        toast.error(t('dashboard.admin.examTabs.errors.fetchFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchTabs();
  }, [t]);

  const handleToggleTab = async (tabId: string) => {
    try {
      const newTabs = tabs.map(tab => 
        tab.id === tabId ? { ...tab, enabled: !tab.enabled } : tab
      );
      setTabs(newTabs);
      
      await setDoc(doc(db, 'settings', 'examTabs'), { tabs: newTabs });
      toast.success(t('dashboard.admin.examTabs.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating exam tabs:', error);
      toast.error(t('dashboard.admin.examTabs.errors.updateFailed'));
      // Revert the change if the update fails
      setTabs(tabs);
    }
  };

  return (
    <Card dir={dir}>
      <CardHeader>
        <CardTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
          {t('dashboard.admin.examTabs.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {tabs.map((tab) => (
            <div 
              key={tab.id} 
              className={`flex items-center justify-between ${
                dir === 'rtl' ? 'flex-row-reverse' : ''
              }`}
            >
              <Label 
                htmlFor={tab.id} 
                className={`flex-1 ${
                  dir === 'rtl' ? 'text-right mr-4' : 'text-left'
                }`}
              >
                {t(`dashboard.examiner.examination.sections.${tab.id}`)}
              </Label>
              <Switch
                id={tab.id}
                checked={tab.enabled}
                onCheckedChange={() => handleToggleTab(tab.id)}
                disabled={loading}
                className={dir === 'rtl' ? 'ml-0 mr-auto' : ''}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
} 